import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Save, X, Loader2 } from "lucide-react";
import { API_BASE, formatApiError } from "./AuthContext";
import { useTeamMembers } from "./AssigneePicker";

// v68.51 — XPM-parity client profile, grouped like Xero Practice Manager.
const SECTIONS = [
  { title: "Client", fields: [
    ["client_code", "Client reference code"], ["title", "Title", "select", ["", "Mr", "Mrs", "Ms", "Miss", "Mx", "Dr", "Prof"]],
    ["first_name", "First name"], ["middle_name", "Middle / other name"], ["last_name", "Last name"],
    ["name", "Display name"], ["gender", "Gender", "select", ["", "Male", "Female", "Other", "Prefer not to say"]],
    ["date_of_death", "Date of death", "date"], ["place_of_birth", "Place of birth"], ["referral_source", "Referral source"],
  ] },
  { title: "Business", fields: [
    ["abn", "ABN"], ["acn", "ACN"], ["balance_date", "Balance date"], ["industry", "Industry"],
  ] },
  { title: "Contact details", fields: [
    ["primary_contact_email", "Email", "email"], ["primary_contact_phone", "Phone"], ["mobile", "Mobile"],
    ["website", "Website"], ["suburb", "Suburb / city"], ["state", "State"], ["postcode", "Postcode"],
    ["country", "Country"], ["postal_address", "Postal address"],
  ] },
  { title: "Team", fields: [["partner_id", "Partner", "member"], ["manager_id", "Manager", "member"]] },
  { title: "Tax", fields: [
    ["branch_number", "Branch / CAC / ICA number"], ["tax_agent", "Tax agent"],
    ["fee_from_refund", "Fee from refund", "bool"], ["active_ato_client", "Active ATO client", "bool"],
    ["prepare_activity_statement", "Prepare activity statement", "bool"], ["prepare_tax_return", "Prepare tax form", "bool"],
    ["lodgment_due_date", "Lodgment due date", "date"], ["financial_institution", "Financial institution"],
    ["bank_account_name", "Account name"], ["director_id", "Director ID"],
  ] },
  { title: "Billing", fields: [
    ["billing_client_type", "Client type"], ["zero_rated_gst", "Zero rated GST", "bool"], ["billing_details", "Billing details"],
  ] },
  { title: "Other", fields: [["groups", "Groups", "list"], ["export_code", "Export code"]] },
];

const showVal = (v, kind, members) => {
  if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) return "—";
  if (kind === "bool") return v ? "Yes" : "No";
  if (kind === "list") return v.join(", ");
  if (kind === "member") return members.find((m) => m.user_id === v)?.name || v;
  return String(v);
};

function FieldInput({ f, value, onChange, members }) {
  const [key, , kind, opts] = f;
  const cls = "w-full bg-white border border-[var(--border)] rounded px-2 py-1.5 text-[12px] focus:outline-none focus:border-[var(--ink)]";
  const tid = `client-profile-input-${key}`;
  if (kind === "select") return <select className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>{opts.map((o) => <option key={o} value={o}>{o || "—"}</option>)}</select>;
  if (kind === "bool") return (
    <select className={cls} value={value === true ? "yes" : value === false ? "no" : ""} data-testid={tid}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "yes")}>
      <option value="">—</option><option value="yes">Yes</option><option value="no">No</option>
    </select>
  );
  if (kind === "member") return (
    <select className={cls} value={value || ""} onChange={(e) => onChange(e.target.value || null)} data-testid={tid}>
      <option value="">Unassigned</option>
      {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
    </select>
  );
  if (kind === "list") return <input className={cls} value={(value || []).join(", ")} data-testid={tid} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />;
  return <input type={kind || "text"} className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
}

export default function ClientProfilePanel({ client, onSaved }) {
  const members = useTeamMembers();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const custom = client.custom_fields || {};

  const start = () => { setDraft({ ...client }); setEditing(true); };
  const save = async () => {
    const patch = {};
    SECTIONS.forEach((s) => s.fields.forEach(([k]) => {
      const a = draft[k] ?? null; const b = client[k] ?? null;
      if (JSON.stringify(a) !== JSON.stringify(b)) patch[k] = a === "" ? null : a;
    }));
    if (patch.name === null) delete patch.name;
    if (!Object.keys(patch).length) { setEditing(false); return; }
    setBusy(true);
    try {
      const r = await axios.patch(`${API_BASE}/clients/${client.client_id}`, patch);
      toast.success("Profile saved"); setEditing(false); onSaved?.(r.data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="p-5 space-y-5" data-testid="client-profile-panel">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-[var(--ink-3)]">Date of birth, TFN, address and bank numbers live in the <b>Sensitive</b> tab.</div>
        {editing ? (
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} className="btn-secondary !py-1 !px-2.5 text-[12px]" data-testid="client-profile-cancel"><X size={12} /> Cancel</button>
            <button onClick={save} disabled={busy} className="btn-primary !py-1 !px-2.5 text-[12px]" data-testid="client-profile-save">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </button>
          </div>
        ) : (
          <button onClick={start} className="btn-secondary !py-1 !px-2.5 text-[12px]" data-testid="client-profile-edit"><Pencil size={12} /> Edit profile</button>
        )}
      </div>
      {SECTIONS.map((s) => (
        <section key={s.title} data-testid={`client-profile-section-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="overline text-[10px] mb-2">{s.title}</div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            {s.fields.map((f) => (
              <div key={f[0]}>
                <dt className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] mb-0.5">{f[1]}</dt>
                <dd className="text-[13px] text-[var(--ink)] break-words" data-testid={`client-profile-${f[0]}`}>
                  {editing ? <FieldInput f={f} members={members} value={draft[f[0]]} onChange={(v) => setDraft((d) => ({ ...d, [f[0]]: v }))} />
                    : showVal(client[f[0]], f[2], members)}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
      {Object.keys(custom).length > 0 && (
        <section data-testid="client-profile-section-custom">
          <div className="overline text-[10px] mb-2">Custom fields (imported)</div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            {Object.entries(custom).map(([k, v]) => (
              <div key={k}>
                <dt className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] mb-0.5">{k}</dt>
                <dd className="text-[13px] text-[var(--ink)]">{String(v)}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </div>
  );
}
